import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, Observable, throwError } from 'rxjs';

import { HttpCommunicationService } from './http-communication.service';
import { ResponseInterceptor } from './model/response-interceptor.interface';

@Injectable()
export class ExecutorService {
    // TODO: public state
    responseInterceptor?: ResponseInterceptor;

    constructor(private readonly communicationService: HttpCommunicationService) {}

    invokeServiceWithParams<T>(
        url: string,
        params?: Record<string, unknown>,
        options: ExecutorOptions = { catchError: true },
    ): Observable<T> {
        const result = this.communicationService.fireService<T>(removeRedundantSlashesFromUrl(url), params);
        return options.catchError ?
            result.pipe(catchError((error: HttpErrorResponse) => this.onError<T>(error, params))) :
            result;
    }

    invokeServiceWithParamsAndReturnFullResponse<T>(
        url: string,
        params?: Record<string, unknown>,
        options: ExecutorOptions = { catchError: true },
    ): Observable<HttpResponse<T>> {
        const result = this.communicationService.fireServiceWithFullResponse<T>(
            removeRedundantSlashesFromUrl(url),
            params);
        return options.catchError ?
            result.pipe(catchError((error: HttpErrorResponse) => this.onError<HttpResponse<T>>(error, params))) :
            result;
    }

    invokeServiceWithBody<T, TBody = unknown>(
        url: string,
        method: string,
        body: TBody,
        options: ExecutorOptions = { catchError: true },
    ): Observable<T> {
        const result = this.communicationService.fireServiceWithBody<T>(
            removeRedundantSlashesFromUrl(url),
            method,
            body);

        return options.catchError ?
            result.pipe(catchError((error: HttpErrorResponse) => this.onError<T>(error, undefined))) :
            result;
    }

    invokeServiceAndReturnFile(
        url: string,
        body: unknown,
        options: ExecutorOptions = { catchError: true },
    ): Observable<HttpResponse<Blob>> {
        const result = this.communicationService.fetchFileFromService(
            removeRedundantSlashesFromUrl(url),
            body);

        return options.catchError ?
            result.pipe(catchError((error: HttpErrorResponse) => this.onError<HttpResponse<Blob>>(error, undefined))) :
            result;
    }

    invokeServiceWithBodyAndReturnFullResponse<T>(
        url: string,
        method: string,
        body: unknown,
        options: ExecutorOptions = { catchError: true },
    ): Observable<HttpResponse<T>> {
        const result = this.communicationService.fetchFullResponseFromService<T>(
            removeRedundantSlashesFromUrl(url),
            method,
            body);

        return options.catchError ?
            result.pipe(catchError((error: HttpErrorResponse) => this.onError<HttpResponse<T>>(error, undefined))) :
            result;
    }

    invokeService<T>(
        url: string,
        method: string,
        options: ExecutorOptions = { catchError: true },
    ): Observable<T> {
        const result: Observable<T> = this.communicationService.fireServiceWithBody(
            removeRedundantSlashesFromUrl(url),
            method,
            null);

        return options.catchError ?
            result.pipe(catchError((error: HttpErrorResponse) => this.onError<T>(error))) :
            result;
    }

    registerInterceptors(responseInterceptor: ResponseInterceptor): void {
        this.responseInterceptor = responseInterceptor;
        this.communicationService.registerInterceptors(responseInterceptor);
    }

    onError<T>(error: HttpErrorResponse | string, requestParameters?: Record<string, unknown>): Observable<T> {
        if (this.responseInterceptor) {
            if (typeof error === 'string') {
                this.responseInterceptor.handleError?.(error);
                return throwError(() => new Error(error));
            } else {
                const errorDetails = this.communicationService.getErrorResponseDetail(error);
                errorDetails.requestParameters = requestParameters;
                this.responseInterceptor.handleException(errorDetails);

                return throwError(() => errorDetails);
            }
        }

        return throwError(() => error);
    }
}

export function removeRedundantSlashesFromUrl(urlString: string): string {
    if (urlString.includes('://')) {
        const url = new URL(urlString);
        url.pathname = removeRedundantSlashesFromPath(url.pathname);
        return url.toString();
    } else {
        return removeRedundantSlashesFromPath(urlString);
    }
}

export function removeRedundantSlashesFromPath(url: string): string {
    // regex to remove // or /// from the URI
    return url.replace(/\/{2,}/g, '/');
}

export interface ExecutorOptions {
    catchError?: boolean;
    responseType?: 'json';
}
